/**
 * Share-of-workforce display semantics, shared by BOTH sides of the assistant
 * turn loop (same pattern as `sentiment-aspects`):
 *
 *  - the dashboard app's `Line.tsx` + chart digest use the predicate to decide
 *    when a chart PAINTS shares (and must therefore be digested as shares);
 *  - apps/dash-meta's single-chart generation uses the SAME predicate to know
 *    when the compiled chart will display shares, so it can prune the
 *    same-dimension filters the parallel extractor resolved (a "share in the
 *    US" ask must NOT land with a US filter — filtering to the asked segment
 *    makes its share 100% by construction).
 *
 * Apps cannot import each other, so before this module each side would have
 * hand-mirrored the predicate and nothing would catch drift until a live
 * demo did.
 */

/**
 * Whether a line chart's headcount values are DISPLAYED as shares of the
 * breakdown total ("Share of Segment" semantics) rather than absolute counts.
 *
 * Semantics mirror the dashboard's `Line.tsx` effective display: an explicit
 * `displayAs` wins ('share' → shares, 'value'/'auto' → counts); with no
 * explicit choice, a breakdown line over headcount DEFAULTS to shares (the
 * composition pages' convention). Only `line` + `headcount` ever displays
 * shares — every other chart/metric pairing plots values.
 */
export const isHeadcountShareDisplayedOnLine = ({
  chartType,
  metric,
  secondaryDimension,
  displayAs,
}: {
  chartType: string | undefined;
  metric: string | undefined;
  secondaryDimension: string | undefined;
  displayAs: 'value' | 'share' | 'auto' | undefined;
}): boolean => {
  if (chartType !== 'line' || metric !== 'headcount') {
    return false;
  }
  if (displayAs !== undefined) {
    return displayAs === 'share';
  }
  return (
    secondaryDimension !== undefined && secondaryDimension !== 'timeseries'
  );
};

/**
 * Expression-tree leaf `kind`s that constrain each breakdown dimension, for
 * pruning same-dimension filters off a share-displayed chart. Mirrors the
 * dashboard digest's `EXPR_KIND_TO_FILTER_ID` mapping (turn-outcome-digest),
 * inverted per secondary dimension. `company` is deliberately absent: company
 * scope arrives via `companyNames`, is the chart's anchor, and a company
 * breakdown on a company chart is excluded upstream.
 */
export const BREAKDOWN_DIMENSION_TO_EXPR_KINDS: Record<string, string[]> = {
  geography: [
    'CITY',
    'COUNTRY',
    'MSA',
    'REGION',
    'STATE',
    'USER_CITY',
    'USER_COUNTRY',
    'USER_MSA',
    'USER_REGION',
    'USER_STATE',
  ],
  gender: ['GENDER'],
  ethnicity: ['ETHNICITY'],
  education: ['HIGHEST_DEGREE'],
  seniority: ['SENIORITY'],
  skills: [
    'SKILL_LVL_1',
    'SKILL_LVL_2',
    'SKILL_LVL_3',
    'SKILL_LVL_4',
    'SKILL_LVL_5',
  ],
  role: ['ROLE_K7', 'ROLE_K150', 'ROLE_K1500', 'ROLE_K15000', 'ROLE_K17000'],
  rics: ['RICS_K10', 'RICS_K50', 'RICS_K400'],
};

/**
 * What the user asked to SEE, classified by the intent model in any language:
 * a level ("how many engineers") or a part of a whole ("what share / fraction /
 * proportion of employees are engineers", "quelle part…"). Both sides of the
 * turn loop compare this enum — dash-meta to repair a share ask that compiled
 * without a share shape, the dashboard to warn the model when the rendered
 * chart still plots absolute values.
 */
export const CHART_VALUE_FORMS = ['absolute', 'share_of_total'] as const;

export type ChartValueForm = (typeof CHART_VALUE_FORMS)[number];
